import moment from "moment";
import { useContext, useMemo, useState } from "react";
import DataTableBase from "../../common/Datatable";
import AnomaliesAddNotes from "./AddNotes";
import TrainerContext from "../../../../context/trainerContext/TrainerContext";
import { cgmService } from "../../../../api/services";
import { useParams } from "react-router-dom";
import makeRequest from "../../../../api";
import { toast } from "react-toastify";
import AnomaliesViewNotes from "./ViewNotes";

const UserAnomalies = ({
  anomaliesList,
  getCGMData,
}: {
  anomaliesList: any;
  getCGMData: CallableFunction;
}) => {
  const params = useParams();
  const userId = params.userId || undefined;

  const { getTrainerState } = useContext(TrainerContext);
  const trainerData = getTrainerState();

  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showViewNotesModal, setShowViewNotesModal] = useState(false);
  const [timestampForNotes, setTimestampForNotes] = useState(0);
  const [notesToView, setNotesToView] = useState([]);

  const columns = useMemo(
    () => [
      {
        name: "Time",
        selector: (row: any) => row.timestamp,
        cell: (row: any) => {
          return <div>{moment(row?.timestamp).format("hh:mm a")}</div>;
        },
        sortable: true,
        width: "12.5%",
      },
      {
        name: "Value",
        selector: (row: any) => row?.glucoseValue,
        cell: (row: any) => {
          return <div>{row?.glucoseValue} mg/dL</div>;
        },
        width: "12.5%",
      },
      {
        name: "Type",
        selector: (row: any) => row?.zScore,
        cell: (row: any) => {
          return <div>{row?.zScore > 0 ? "Spike" : "Dip"}</div>;
        },
        width: "12.5%",
      },
      {
        name: "User Notes",
        selector: (row: any) => row?.note,
        cell: (row: any) => {
          return <div>{row?.note}</div>;
        },
        width: "37.5%",
      },
      {
        name: "Action",
        selector: (row: any) => row.userId,
        cell: (row: any) => {
          return (
            <div className="d-flex gap-2">
              {!!JSON.parse(row?.coachNotes)?.length && (
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => {
                    toggleShowViewNotesModal();
                    setNotesToView(JSON.parse(row?.coachNotes));
                  }}
                >
                  View notes
                </button>
              )}
              <button
                className="btn btn-sm btn-outline-success"
                onClick={() => {
                  toggleShowAddNotesModal();
                  setTimestampForNotes(row?.recordedAt);
                }}
              >
                Add note
              </button>
            </div>
          );
        },
        width: "25%",
      },
    ],
    []
  );

  const toggleShowAddNotesModal = () => {
    setShowAddNoteModal((prev: any) => !prev);
  };

  const toggleShowViewNotesModal = () => {
    setShowViewNotesModal((prev: any) => !prev);
  };

  const handleAddNote = async (note: string) => {
    if (
      !userId?.length ||
      !trainerData?.trainerId?.length ||
      timestampForNotes === 0
    ) {
      return;
    }
    const data = {
      recordedAt: timestampForNotes,
      coachNote: {
        createdAt: moment().valueOf(),
        text: note,
        trainerDetails: {
          trainerId: trainerData?.trainerId,
          trainerName: trainerData?.trainerName,
          trainerCategory: trainerData?.trainerCategory,
        },
      },
    };
    try {
      const res: any = await makeRequest(
        cgmService.addCoachNote({ userId }, data, userId)
      );
      toast.success("Note added successfully!");
      toggleShowAddNotesModal();
      getCGMData();
      setTimestampForNotes(0);
    } catch (error: any) {
      toast.error("Error while adding note!");
    }
  };

  if (Array.isArray(anomaliesList) && !anomaliesList.length) {
    return <p className="fw-500 fs-20 mb-0">No data available for this date</p>;
  }

  return (
    <div>
      <h6 className="fw-700 fs-24 mb-3">Anomalies</h6>
      <DataTableBase data={anomaliesList} columns={columns} />
      {showAddNoteModal && (
        <AnomaliesAddNotes
          onClose={toggleShowAddNotesModal}
          onSave={handleAddNote}
        />
      )}
      {showViewNotesModal && (
        <AnomaliesViewNotes
          onClose={toggleShowViewNotesModal}
          notes={notesToView}
        />
      )}
    </div>
  );
};

export default UserAnomalies;
